/**
 * F2-Verify — code entry. Completes the email-OTP exchange for tokens.
 */
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { verifyOtp, requestOtp, getMe, claimContractor, getIdToken, ApiError } from '../lib/api';

interface VerifyState {
  email?: string;
  session?: string;
}

export function Verify(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as VerifyState) ?? {};
  const [email] = useState(state.email ?? '');
  const [session, setSession] = useState(state.session ?? '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!email) {
    // Arrived without a session — send them back to sign in.
    navigate('/', { replace: true });
    return <></>;
  }

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await verifyOtp(email, code.trim(), session);
      // One-time contractor claim: if this email matches a contractor row, this
      // links the login + grants the CONTRACTOR role (so getMe below reflects it).
      const idToken = getIdToken();
      if (idToken) {
        try {
          const claim = await claimContractor(idToken);
          if (claim.linked) {
            navigate('/contractor');
            return;
          }
        } catch {
          // A failed claim must not block login — fall through to role routing.
        }
      }
      // Route by role: staff → admin dashboard; clients → dashboard (which sends
      // them to intake if they have no profile yet).
      const me = await getMe();
      if (me.role === 'ADMINISTRATOR' || me.role === 'ESTIMATOR') {
        navigate('/admin/dashboard');
      } else if (me.role === 'CONTRACTOR') {
        navigate('/contractor');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? 'That code didn\u2019t match. Check it and try again.'
          : 'Something went wrong verifying the code.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function resend(): Promise<void> {
    setError(null);
    try {
      const r = await requestOtp(email);
      setSession(r.session);
    } catch {
      setError('Could not resend the code.');
    }
  }

  return (
    <div className="flow flow--single">
      <div className="stage-card">
        <h1>Check your email.</h1>
        <p className="stage-card__lede">
          We sent a one-time code to <strong>{email}</strong>. Enter it below.
        </p>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--pb-3)' }}>
          <label className="pb-label" htmlFor="code">
            Your code
          </label>
          <input
            id="code"
            className="pb-field"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="6&ndash;8 digits"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          {error ? (
            <div className="pb-note pb-note--flag" role="alert">
              {error}
            </div>
          ) : null}
          <button type="submit" className="pb-btn pb-btn--lg" disabled={busy || !code}>
            {busy ? 'Verifying\u2026' : 'Verify and continue'}
          </button>
          <button type="button" className="progress__back" onClick={resend}>
            Didn&rsquo;t get it? Send another
          </button>
        </form>
      </div>
    </div>
  );
}
